import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react'

type ActionsByKey = {
  [key: string]: () => void
}

type ActionsByKeyStack = {
  [key: string]: (() => void)[]
}

type ShortcutActionsContextType = {
  actionsByKeyStack: ActionsByKeyStack
  setActionsByKey: (actionsByKey: ActionsByKey) => void
  removeActionsByKey: (actionsByKey: ActionsByKey) => void
}

const ShortcutActionsContext = createContext<ShortcutActionsContextType>({
  actionsByKeyStack: {},
  setActionsByKey: () => {},
  removeActionsByKey: () => {},
})

export const ShortcutActionsProvider = ({ children }: { children: ReactNode }) => {
  const [actionsByKeyStack, setActionsByKeyStack] = useState<ActionsByKeyStack>({})

  const setActionsByKey = useCallback((actionsByKey: ActionsByKey) => {
    setActionsByKeyStack((prevActionsByKeyStack) => {
      let newActionsByKeyStack: ActionsByKeyStack = {}
      Object.keys(prevActionsByKeyStack).forEach((key) => {
        newActionsByKeyStack[key] = [...prevActionsByKeyStack[key]]
      })

      Object.entries(actionsByKey).forEach(([key, action]) => {
        if (!newActionsByKeyStack[key]) {
          newActionsByKeyStack[key] = []
        }
        newActionsByKeyStack[key].push(action)
      })

      return newActionsByKeyStack
    })
  }, [])

  const removeActionsByKey = useCallback((actionsByKey: ActionsByKey) => {
    setActionsByKeyStack((prevActionsByKeyStack) => {
      let newActionsByKeyStack: ActionsByKeyStack = {}
      Object.keys(prevActionsByKeyStack).forEach((key) => {
        newActionsByKeyStack[key] = [...prevActionsByKeyStack[key]]
      })

      Object.keys(actionsByKey).forEach((key) => {
        const actions = newActionsByKeyStack[key]
        if (!actions) return

        newActionsByKeyStack[key] = actions.filter((action) => action !== actionsByKey[key])
      })

      return newActionsByKeyStack
    })
  }, [])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const tag = document.activeElement?.tagName
      const isContentEditable = document.activeElement?.getAttribute('contenteditable')
      const isTyping = tag === 'INPUT' || tag === 'TEXTAREA' || isContentEditable === 'true'
      if (isTyping) return

      const actions = actionsByKeyStack[e.code]
      if (!actions) return

      e.preventDefault()
      actions[actions.length - 1]?.()
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [actionsByKeyStack])

  return (
    <ShortcutActionsContext.Provider
      value={{ actionsByKeyStack, setActionsByKey, removeActionsByKey }}
    >
      {children}
    </ShortcutActionsContext.Provider>
  )
}

export const useShortcutActions = (actionsByKey: ActionsByKey) => {
  const { actionsByKeyStack, setActionsByKey, removeActionsByKey } =
    useContext(ShortcutActionsContext)

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log(
        `useShortcutActions initialized. actions: ${Object.keys(actionsByKey).join(', ')}`,
      )
    }

    setActionsByKey(actionsByKey)

    return () => {
      removeActionsByKey(actionsByKey)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionsByKey])

  const isShortcutEnabled = useCallback(
    (key: string) => {
      const actions = actionsByKeyStack[key]
      if (!actions) return false

      return actions[actions.length - 1] === actionsByKey[key]
    },
    [actionsByKeyStack, actionsByKey],
  )

  return { isShortcutEnabled }
}
